import type { HealthData, IntervalsTokenData } from '@shared/types';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import apiClient from '../services/api';

export interface AuthState {
  // Intervals.icu data integration
  isIntervalsConnected: boolean;
  isLoading: boolean;
  tokenData: IntervalsTokenData | null;
  healthData: HealthData | null;
  error: string | null;

  // User identification from Intervals.icu
  userId: string | null;
  isAuthenticated: boolean;
}

export type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | {
      type: 'SET_INTERVALS_CONNECTED';
      payload: { tokenData: IntervalsTokenData; healthData?: HealthData };
    }
  | { type: 'SET_HEALTH_DATA'; payload: HealthData }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  isIntervalsConnected: false,
  isAuthenticated: false,
  isLoading: true,
  tokenData: null,
  healthData: null,
  error: null,
  userId: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_INTERVALS_CONNECTED':
      return {
        ...state,
        isIntervalsConnected: true,
        tokenData: action.payload.tokenData,
        healthData: action.payload.healthData || null,
        userId: action.payload.tokenData.athlete?.id || null,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'SET_HEALTH_DATA':
      return {
        ...state,
        healthData: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'LOGOUT':
      localStorage.removeItem('intervals_token');
      return {
        ...initialState,
        isLoading: false,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

interface AuthContextType {
  state: AuthState;
  login: () => Promise<void>;
  logout: () => void;
  refreshHealthData: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from backend (single source of truth)
  // Backend stores tokens in MongoDB - frontend only caches connection status
  // This function should only be called when user is authenticated with Entra ID
  const checkAuth = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Always verify connection with backend - it's the authoritative source
      // Backend validates the token by making an API call to Intervals.icu
      // Health data is now included in status response to avoid a separate API call
      const statusResponse = await apiClient.getIntervalsStatus();

      if (statusResponse.success && statusResponse.data?.connected) {
        // User has valid Intervals.icu connection stored in backend
        const athleteData = statusResponse.data.athlete;

        // Create token data from backend response (for display purposes only)
        const tokenData: IntervalsTokenData = {
          accessToken: '', // Never stored client-side
          refreshToken: '',
          expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000, // Display only
          scope: '',
          timestamp: Date.now(),
          athlete: {
            id: athleteData?.id || '',
            name: athleteData?.name || '',
            email: athleteData?.email || '',
            premium: false,
            locale: '',
            timezone: '',
          },
        };

        // Health data is now included in status response (no separate API call needed)
        const healthData = statusResponse.data.healthData;

        dispatch({
          type: 'SET_INTERVALS_CONNECTED',
          payload: {
            tokenData,
            healthData: healthData || undefined,
          },
        });

        // Clean up any legacy localStorage tokens
        localStorage.removeItem('intervals_token');
      } else {
        // No valid connection - clean up any stale localStorage
        localStorage.removeItem('intervals_token');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    } catch (error: any) {
      // Handle 401 gracefully - user just isn't authenticated with Entra yet
      // This is expected on the signin page, so don't treat it as an error
      if (error?.response?.status === 401 || error?.message?.includes('401')) {
        localStorage.removeItem('intervals_token');
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      console.error('Auth initialization error:', error);
      localStorage.removeItem('intervals_token');

      // Provide more specific error messages
      let errorMessage = 'Failed to initialize authentication';
      if (error instanceof Error) {
        if (error.message.includes('Network Error') || error.message.includes('ECONNREFUSED')) {
          errorMessage = 'Cannot connect to backend server. Please check if the server is running.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Backend server is not responding. Please try again later.';
        } else {
          errorMessage = `Authentication failed: ${error.message}`;
        }
      }

      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }
  }, []);

  // Don't auto-check on mount - wait for Entra auth to complete first
  // The component using this context should call checkAuth after Entra login
  useEffect(() => {
    // Set loading to false initially - checkAuth will be called by parent after Entra auth
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  // Listen for auth errors from API client
  useEffect(() => {
    const handleAuthError = () => {
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('auth-error', handleAuthError);
    return () => window.removeEventListener('auth-error', handleAuthError);
  }, []);

  const login = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      // Get authorization URL
      const authResponse = await apiClient.getIntervalsAuthUrl();

      if (!authResponse.success) {
        throw new Error(authResponse.error || 'Failed to get authorization URL');
      }

      if (!authResponse.data?.authUrl) {
        throw new Error('No authorization URL provided');
      }

      // Redirect to intervals.icu OAuth
      window.location.href = authResponse.data.authUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  };

  const logout = async (): Promise<void> => {
    dispatch({ type: 'LOGOUT' });
  };

  const refreshHealthData = async (): Promise<void> => {
    if (!state.isAuthenticated) return;

    try {
      const healthResponse = await apiClient.getIntervalsHealth();

      if (healthResponse.success && healthResponse.data) {
        // The health data is nested in health_summary
        const healthData = (healthResponse.data as any).health_summary || healthResponse.data;
        dispatch({ type: 'SET_HEALTH_DATA', payload: healthData });
      } else {
        throw new Error(healthResponse.error || 'Failed to fetch health data');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to refresh health data';
      dispatch({ type: 'SET_ERROR', payload: message });
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    state,
    login,
    logout,
    refreshHealthData,
    clearError,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
